import { useState, useCallback } from 'react';
import { useNavigate } from 'react-router';
import { ArrowLeft, CheckCheck, XCircle, Filter, CreditCard } from 'lucide-react';
import {
  usePendingTransactions,
  useWalletTransactions,
  useApproveTransactions,
  useDismissTransactions,
} from '@/hooks/useWallet';
import { useCategories } from '@/hooks/useCategories';
import { resolveWalletCategoryForTransaction } from '@/lib/walletCategoryMap';
import { WalletTransactionCard } from '@/components/wallet/WalletTransactionCard';
import type { WalletTransaction } from '@/api/wallet';
import { useTranslation } from 'react-i18next';

type StatusFilter = 'pending' | 'approved' | 'dismissed' | 'all';

export function WalletTransactionsPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [statusFilter, setStatusFilter] = useState<StatusFilter>('pending');
  const [accountFilter, setAccountFilter] = useState<string>('all');
  // Per-transaction category overrides: txId → categoryId (null = no category, undefined = use default)
  const [categoryOverrides, setCategoryOverrides] = useState<Map<string, string | null>>(new Map());

  // Queries
  const { data: pendingTransactions = [], isLoading: pendingLoading } = usePendingTransactions();
  const { data: allTransactions = [], isLoading: allLoading } = useWalletTransactions(
    statusFilter === 'all' ? undefined : statusFilter,
  );
  const { data: categories = [] } = useCategories();

  // Mutations
  const approveMutation = useApproveTransactions();
  const dismissMutation = useDismissTransactions();

  const unfilteredTransactions = statusFilter === 'pending' ? pendingTransactions : allTransactions;
  const isLoading = statusFilter === 'pending' ? pendingLoading : allLoading;

  // Derive unique accounts for filter (from unfiltered list)
  const uniqueAccounts = Array.from(
    new Map(
      unfilteredTransactions
        .filter(t => t.mapping?.wallet_account_name)
        .map(t => [t.wallet_account_id, t.mapping!.wallet_account_name!]),
    ).entries(),
  );
  const hasMultipleAccounts = uniqueAccounts.length > 1;

  const transactions = accountFilter === 'all'
    ? unfilteredTransactions
    : unfilteredTransactions.filter(t => t.wallet_account_id === accountFilter);

  const toggleSelect = (id: string) => {
    setSelectedIds(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const selectAll = () => {
    const pendingIds = transactions
      .filter(t => t.status === 'pending')
      .map(t => t.id);
    setSelectedIds(new Set(pendingIds));
  };

  const clearSelection = () => setSelectedIds(new Set());

  const handleCategoryChange = useCallback((txId: string, categoryId: string | null) => {
    setCategoryOverrides(prev => {
      const next = new Map(prev);
      next.set(txId, categoryId);
      return next;
    });
  }, []);

  /**
   * Resolve the effective category_id for a transaction:
   *   1. User override (from categoryOverrides state)
   *   2. Map resolution (Wallet category → envelope id → ShareCost key → category.id)
   *   3. null (no category)
   */
  const getEffectiveCategoryId = useCallback((tx: WalletTransaction): string | null => {
    if (categoryOverrides.has(tx.id)) {
      return categoryOverrides.get(tx.id) ?? null;
    }
    const key = resolveWalletCategoryForTransaction(tx);
    if (!key) return null;
    return categories.find(c => c.key === key)?.id ?? null;
  }, [categoryOverrides, categories]);

  const BATCH_SIZE = 50;

  const handleApprove = async (ids: string[]) => {
    try {
      // Group transactions by their effective category so we can send correct overrides
      const txById = new Map(transactions.map(t => [t.id, t]));
      const byCategoryId = new Map<string, string[]>(); // categoryId (or '__none__') → txIds

      for (const id of ids) {
        const tx = txById.get(id);
        if (!tx) continue;
        const catId = getEffectiveCategoryId(tx);
        const groupKey = catId ?? '__none__';
        const arr = byCategoryId.get(groupKey) ?? [];
        arr.push(id);
        byCategoryId.set(groupKey, arr);
      }

      // Send one batch per distinct category
      for (const [groupKey, txIds] of byCategoryId) {
        const overrides = groupKey !== '__none__' ? { category_id: groupKey } : undefined;
        for (let i = 0; i < txIds.length; i += BATCH_SIZE) {
          const batch = txIds.slice(i, i + BATCH_SIZE);
          await approveMutation.mutateAsync({ transactionIds: batch, overrides });
        }
      }

      // Clean up state for approved transactions
      setSelectedIds(prev => {
        const next = new Set(prev);
        ids.forEach(id => next.delete(id));
        return next;
      });
      setCategoryOverrides(prev => {
        const next = new Map(prev);
        ids.forEach(id => next.delete(id));
        return next;
      });
    } catch {
      // Error handled by React Query
    }
  };

  const handleDismiss = async (ids: string[]) => {
    try {
      for (let i = 0; i < ids.length; i += BATCH_SIZE) {
        const batch = ids.slice(i, i + BATCH_SIZE);
        await dismissMutation.mutateAsync(batch);
      }
      setSelectedIds(prev => {
        const next = new Set(prev);
        ids.forEach(id => next.delete(id));
        return next;
      });
      setCategoryOverrides(prev => {
        const next = new Map(prev);
        ids.forEach(id => next.delete(id));
        return next;
      });
    } catch {
      // Error handled by React Query
    }
  };

  const pendingCount = pendingTransactions.length;
  const hasSelection = selectedIds.size > 0;

  return (
    <div className="mx-auto max-w-2xl space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <button
            onClick={() => navigate('/wallet')}
            className="rounded-lg p-1 text-text-quaternary hover:text-text-secondary"
          >
            <ArrowLeft className="h-5 w-5" />
          </button>
          <h1 className="text-2xl font-bold text-text-primary">{t('web:walletTransactionsPage.walletTransactions')}</h1>
          {pendingCount > 0 && (
            <span className="rounded-full bg-amber-100 dark:bg-amber-900/30 px-2.5 py-0.5 text-sm font-medium text-amber-700 dark:text-amber-400">
              {t('web:walletTransactionsPage.pendingCount', { count: pendingCount })}
            </span>
          )}
        </div>
      </div>

      {/* Filter tabs */}
      <div className="flex items-center gap-1 rounded-lg bg-surface-tertiary p-1">
        {(['pending', 'approved', 'dismissed', 'all'] as const).map(filter => (
          <button
            key={filter}
            onClick={() => { setStatusFilter(filter); clearSelection(); }}
            className={`flex-1 rounded-md px-3 py-1.5 text-sm font-medium transition-colors ${
              statusFilter === filter
                ? 'bg-surface-primary text-text-primary shadow-sm'
                : 'text-text-tertiary hover:text-text-secondary'
            }`}
          >
            {filter.charAt(0).toUpperCase() + filter.slice(1)}
          </button>
        ))}
      </div>

      {/* Account filter (only when multiple accounts) */}
      {hasMultipleAccounts && (
        <div className="flex items-center gap-2">
          <CreditCard className="h-4 w-4 text-text-quaternary" />
          <div className="flex items-center gap-1 rounded-lg bg-surface-tertiary p-1">
            <button
              onClick={() => { setAccountFilter('all'); clearSelection(); }}
              className={`rounded-md px-3 py-1 text-xs font-medium transition-colors ${
                accountFilter === 'all'
                  ? 'bg-surface-primary text-text-primary shadow-sm'
                  : 'text-text-tertiary hover:text-text-secondary'
              }`}
            >
              {t('web:walletTransactionsPage.allAccounts')}
            </button>
            {uniqueAccounts.map(([accountId, name]) => (
              <button
                key={accountId}
                onClick={() => { setAccountFilter(accountId); clearSelection(); }}
                className={`rounded-md px-3 py-1 text-xs font-medium transition-colors ${
                  accountFilter === accountId
                    ? 'bg-surface-primary text-text-primary shadow-sm'
                    : 'text-text-tertiary hover:text-text-secondary'
                }`}
              >
                {name}
              </button>
            ))}
          </div>
        </div>
      )}

      {/* Batch actions bar */}
      {statusFilter === 'pending' && pendingCount > 0 && (
        <div className="flex items-center justify-between rounded-lg bg-surface-secondary px-4 py-2">
          <div className="flex items-center gap-3">
            <button
              onClick={hasSelection ? clearSelection : selectAll}
              className="text-sm font-medium text-brand-600 dark:text-brand-400 hover:text-brand-700 dark:hover:text-brand-300"
            >
              {hasSelection ? t('web:common.clearSelection') : t('expenses:importedTransactions.selectAll')}
            </button>
            {hasSelection && (
              <span className="text-sm text-text-tertiary">
                {selectedIds.size} selected
              </span>
            )}
          </div>

          {hasSelection && (
            <div className="flex items-center gap-2">
              <button
                onClick={() => handleApprove(Array.from(selectedIds))}
                disabled={approveMutation.isPending}
                className="flex items-center gap-1.5 rounded-lg bg-green-600 px-3 py-1.5 text-sm font-medium text-white hover:bg-green-700 disabled:opacity-50 transition-colors"
              >
                <CheckCheck className="h-4 w-4" />
                {approveMutation.isPending ? t('web:walletTransactionsPage.approving') : t('web:common.approve')}
              </button>
              <button
                onClick={() => handleDismiss(Array.from(selectedIds))}
                disabled={dismissMutation.isPending}
                className="flex items-center gap-1.5 rounded-lg border border-border-secondary px-3 py-1.5 text-sm text-text-secondary hover:bg-surface-hover disabled:opacity-50 transition-colors"
              >
                <XCircle className="h-4 w-4" />
                {dismissMutation.isPending ? t('web:walletTransactionsPage.dismissing') : t('expenses:importedTransactions.action.dismiss')}
              </button>
            </div>
          )}
        </div>
      )}

      {/* Error display */}
      {approveMutation.isError && (
        <div className="rounded-lg bg-red-50 dark:bg-red-900/30 p-3 text-sm text-red-700 dark:text-red-400">
          {t('web:walletTransactionsPage.failedToApprove', { message: (approveMutation.error as Error)?.message ?? t('web:common.unknownError') })}
        </div>
      )}

      {/* Transaction list */}
      {isLoading ? (
        <div className="flex items-center justify-center p-12">
          <div className="h-8 w-8 animate-spin rounded-full border-4 border-brand-600 border-t-transparent" />
        </div>
      ) : transactions.length === 0 ? (
        <div className="rounded-xl bg-surface-primary p-12 text-center shadow-sm ring-1 ring-border-primary">
          <Filter className="mx-auto h-8 w-8 text-text-muted" />
          <p className="mt-3 text-sm text-text-tertiary">
            {statusFilter === 'pending'
              ? t('web:walletTransactionsPage.noPendingTransactionsRunASync')
              : t('web:walletTransactionsPage.noTransactionsFound')}
          </p>
        </div>
      ) : (
        <div className="space-y-2">
          {transactions.map(tx => (
            <WalletTransactionCard
              key={tx.id}
              transaction={tx}
              isSelected={selectedIds.has(tx.id)}
              categories={categories}
              categoryOverride={categoryOverrides.has(tx.id) ? categoryOverrides.get(tx.id)! : undefined}
              onToggleSelect={toggleSelect}
              onApprove={id => handleApprove([id])}
              onDismiss={id => handleDismiss([id])}
              onCategoryChange={handleCategoryChange}
            />
          ))}
        </div>
      )}
    </div>
  );
}
